import React, { useEffect, useMemo, useState } from 'react';
import { addMonths, format, isSameMonth, startOfMonth } from 'date-fns';
import { CalendarDays, ChevronLeft, ChevronRight, Loader2, RefreshCw } from 'lucide-react';
import { PostCalendarGrid } from './PostCalendarGrid';
import { PostCalendarAgenda } from './PostCalendarAgenda';
import { useCalendarData } from './useCalendarData';
import { dayRange, monthRange, monthKey, queryString, statusLabels, statusColors } from './calendarUtils';

export const PostCalendarTab = ({ fetchAdmin, profileId, onProfileChange }) => {
  const [month, setMonth] = useState(() => startOfMonth(new Date()));
  const [selected, setSelected] = useState(() => new Date());
  const [filters, setFilters] = useState({ status: 'all', platform: 'all' });
  const [page, setPage] = useState(1);
  const [revision, setRevision] = useState(0);
  const timezone = useMemo(() => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC', []);
  const common = { ...filters, user_id: profileId };
  const profiles = useCalendarData(fetchAdmin, '/api/admin-panel/post-profiles', revision);
  const summary = useCalendarData(fetchAdmin, `/api/admin-panel/posts/calendar?${queryString({ ...common, ...monthRange(month), timezone })}`, revision);
  const agenda = useCalendarData(fetchAdmin, `/api/admin-panel/all-posts?${queryString({ ...common, ...dayRange(selected), limit: 24, page })}`, revision);
  useEffect(() => {
    if (!agenda.loading && agenda.data && agenda.data.page !== page) setPage(agenda.data.page);
  }, [agenda.loading, agenda.data, page]);
  const counts = useMemo(() => Object.fromEntries((summary.data?.days || []).map(day => [day.date, day])), [summary.data]);
  const refresh = () => setRevision(value => value + 1);
  const pickMonth = date => {
    if (Number.isNaN(date.getTime())) return;
    const next = startOfMonth(date);
    setMonth(next);
    setSelected(isSameMonth(next, new Date()) ? new Date() : next);
    setPage(1);
  };
  const changeFilter = (field, value) => { setFilters(prev => ({ ...prev, [field]: value })); setPage(1); };
  const refreshing = summary.refreshing || agenda.refreshing;
  const selectClass = 'max-w-full min-w-0 w-full sm:w-auto rounded-lg border border-white/10 bg-[#141414] text-white/80 text-sm px-3 py-2 focus:border-emerald-500 focus:outline-none';

  return (
    <section data-testid="admin-calendar-tab" className="min-w-0">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-5">
        <div>
          <h2 className="text-white text-lg font-semibold flex items-center gap-2"><CalendarDays size={20} className="text-emerald-400" /> Calendar</h2>
          <div className="text-white/45 text-xs mt-1 flex flex-wrap gap-x-2 gap-y-1">
            <span data-testid="calendar-month-total">{summary.loading ? 'Loading…' : summary.error && !summary.data ? 'Month unavailable' : `${summary.data?.total ?? 0} ${(summary.data?.total ?? 0) === 1 ? 'post' : 'posts'} this month`}</span>
            <span data-testid="calendar-timezone">{timezone}</span>
          </div>
        </div>
        <button data-testid="calendar-refresh" onClick={refresh} aria-label="Refresh calendar" title="Refresh calendar" className="p-2 bg-white/5 hover:bg-white/10 rounded-lg text-white/70 transition-colors">
          {refreshing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />}
        </button>
      </div>

      <div className="flex flex-col sm:flex-row flex-wrap gap-2 mb-5">
        <select data-testid="calendar-filter-profile" aria-label="Calendar profile" value={profileId}
          onChange={e => { onProfileChange(e.target.value); setPage(1); }} className={selectClass}>
          <option value="all">All profiles</option>
          {(profiles.data?.users || []).map(user => <option key={user.user_id} value={user.user_id}>{user.email || user.name || user.user_id}</option>)}
        </select>
        <select data-testid="calendar-filter-status" aria-label="Calendar status" value={filters.status}
          onChange={e => changeFilter('status', e.target.value)} className={selectClass}>
          <option value="all">All statuses</option>
          {Object.entries(statusLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <select data-testid="calendar-filter-platform" aria-label="Calendar platform" value={filters.platform}
          onChange={e => changeFilter('platform', e.target.value)} className={selectClass}>
          <option value="all">All platforms</option>
          {['tiktok', 'instagram', 'facebook', 'youtube', 'x', 'linkedin', 'threads', 'pinterest', 'bluesky'].map(platform => <option key={platform} value={platform}>{platform === 'x' ? 'X (Twitter)' : platform[0].toUpperCase() + platform.slice(1)}</option>)}
        </select>
      </div>

      {(summary.error || profiles.error) && <div data-testid="calendar-load-error" role="alert" className="text-red-300 text-sm mb-4">
        {summary.error || profiles.error} <button data-testid="calendar-retry" onClick={refresh} className="underline ml-2">Retry</button>
      </div>}

      <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.55fr)_minmax(280px,1fr)] gap-6 min-w-0">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <h3 data-testid="calendar-month-heading" className="text-white font-medium text-base">{format(month, 'MMMM yyyy')}</h3>
            <div className="flex flex-wrap items-center gap-1.5">
              <button data-testid="calendar-prev-month" aria-label="Previous month" title="Previous month" onClick={() => pickMonth(addMonths(month, -1))} className="p-2 rounded-lg bg-white/5 text-white/70 hover:bg-white/10"><ChevronLeft size={16} /></button>
              <button data-testid="calendar-today" onClick={() => { setMonth(startOfMonth(new Date())); setSelected(new Date()); setPage(1); }} className="text-xs px-2 py-2 rounded-lg bg-white/5 text-white/80 hover:bg-white/10">Today</button>
              <button data-testid="calendar-next-month" aria-label="Next month" title="Next month" onClick={() => pickMonth(addMonths(month, 1))} className="p-2 rounded-lg bg-white/5 text-white/70 hover:bg-white/10"><ChevronRight size={16} /></button>
            </div>
          </div>
          <label className="flex items-center gap-2 text-xs text-white/40 mb-3">
            Month
            <input type="month" data-testid="calendar-month-picker" aria-label="Go to month" value={monthKey(month)}
              onChange={e => { if (/^\d{4}-\d{2}$/.test(e.target.value)) pickMonth(new Date(`${e.target.value}-01T12:00:00`)); }}
              className="max-w-full min-w-0 bg-transparent border-b border-white/15 text-white/70 py-1 [color-scheme:dark]" />
          </label>
          <PostCalendarGrid month={month} selected={selected} onSelect={date => { setSelected(date); setPage(1); }} onMonthChange={pickMonth} counts={counts} loading={summary.loading} />
          {summary.loading && <p data-testid="calendar-loading" role="status" className="text-white/45 text-xs mt-2 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading month…</p>}
          <div data-testid="calendar-status-legend" className="flex flex-wrap gap-x-4 gap-y-2 text-[11px] text-white/50 mt-3">
            {Object.entries(statusLabels).map(([value, label]) => <span key={value} className="inline-flex items-center gap-1.5"><i className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: statusColors[value] }} />{label}</span>)}
          </div>
        </div>
        <PostCalendarAgenda date={selected} state={agenda} onPage={setPage} onRetry={refresh} />
      </div>
    </section>
  );
};